import React from 'react'
import type {
  AccordionItemProps,
  AccordionContentProps,
} from '@radix-ui/react-accordion'
import { Root, Item, Trigger, Content, Header } from '@radix-ui/react-accordion'
import { ChevronRightIcon } from '@radix-ui/react-icons'
import { Text } from '@components/Text'
import type { TextVariants } from '@components/Text/index.variants'
import { Link } from '@components/Link'
import type { HeaderLink } from '@cms/data/getHeaderData'
import { LinkForAppFeatures } from '@components/LinkForAppFeatures'
import { NewFooterTag } from '@components/Footer/FooterNavigationItems'
import { trackGTM } from '@helpers/core/trackGTM'
import { useEventTracker } from '@hooks/useEventTracker'

export type AccordionGroupProps = {
  label: string
  links: Array<HeaderLink>
  new?: boolean
}

type AccordionProps = {
  items: Array<AccordionGroupProps>
  children?: React.ReactNode
  showBorders?: boolean
  triggerTextVariant?: TextVariants
  trackingEvent?: string | null | undefined
}

type ComponentProps = {
  children: React.ReactNode
}

const handleClick = (
  link: string,
  text: string,
  groupName?: string | null | undefined,
  trackingEvent?: string | null | undefined
) => {
  const isPDFDownload = link?.includes('.pdf')

  if (isPDFDownload) {
    trackGTM({
      event: 'pdf_download_click',
      text,
      // eslint-disable-next-line camelcase
      destination_url: link,
    })
  } else if (groupName && trackingEvent) {
    trackGTM({
      event: trackingEvent,
      text,
      group: groupName,
    })
  }
}

const AccordionItem = React.forwardRef<
  HTMLDivElement,
  ComponentProps & AccordionItemProps
>(({ children, ...props }, forwardedRef) => (
  // Accordion Item renders H3 need to find a way to use nxd component with accordion item
  <Item {...props} ref={forwardedRef}>
    {children}
  </Item>
))

AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<HTMLButtonElement, ComponentProps>(
  ({ children, ...props }, forwardedRef) => (
    <Header className='flex'>
      <Trigger
        className='text-neutral font-sans p-12 group flex flex-1 justify-between outline-teal'
        {...props}
        ref={forwardedRef}
      >
        {children}
        <ChevronRightIcon
          className='text-neutral group-data-[state=open]:rotate-90'
          aria-hidden
        />
      </Trigger>
    </Header>
  )
)

AccordionTrigger.displayName = 'AccordionTrigger'

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  ComponentProps & AccordionContentProps
>(({ children, className, ...props }, forwardedRef) => (
  <Content
    className={`text-neutral font-sans px-12 bg-neutral ${className || ''}`}
    {...props}
    ref={forwardedRef}
  >
    <div className='py-12 px-5'>{children}</div>
  </Content>
))

const AccordionLinks = ({
  links,
  className,
  groupName,
  trackingEvent,
}: {
  links: HeaderLink[]
  className?: string
  groupName?: string | null | undefined
  trackingEvent?: string | null | undefined
}) => {
  const { track } = useEventTracker()

  return (
    <>
      {links.map(link => {
        return (
          <AccordionContent
            className={className}
            key={`${link.href}-${link.label}`}
          >
            {/* We need to localise the BlogCategory url */}
            {link.featureType === 'BlogCategory' ? (
              <LinkForAppFeatures
                type='BlogCategory'
                slug={link.href}
                onClick={() => {
                  if (link.snowplowTrackingData) {
                    track(link.snowplowTrackingData)
                  }

                  handleClick(link.href, link.label, groupName, trackingEvent)
                }}
              >
                <Text
                  variant={
                    link.textVariant === 'standard-bold'
                      ? 'standard-bold'
                      : 'standard'
                  }
                  color={link.textColor === 'teal' ? 'teal' : 'black'}
                >
                  {link.label}
                </Text>
                {link.new && <NewFooterTag />}
              </LinkForAppFeatures>
            ) : (
              <Link
                url={link.href}
                onClick={() => {
                  if (link.snowplowTrackingData) {
                    track(link.snowplowTrackingData)
                  }

                  handleClick(link.href, link.label, groupName, trackingEvent)
                }}
              >
                <Text
                  variant={
                    link.textVariant === 'standard-bold'
                      ? 'standard-bold'
                      : 'standard'
                  }
                  color={link.textColor === 'teal' ? 'teal' : 'black'}
                >
                  {link.label}
                </Text>
                {link.new && <NewFooterTag />}
              </Link>
            )}
          </AccordionContent>
        )
      })}
    </>
  )
}

AccordionContent.displayName = 'AccordionContent'

export const Accordion = ({
  items = [],
  children,
  showBorders = false,
  triggerTextVariant,
  trackingEvent,
}: AccordionProps) => {
  const rootBorderClasses = showBorders
    ? 'border rounded-md border-neutral'
    : ''
  const itemBorderClasses = showBorders
    ? 'border-b border-neutral last:border-b-0 py-8'
    : ''
  const linkBorderClasses = showBorders
    ? 'border-b border-neutral last:border-b-0 py-4'
    : ''

  return (
    <Root className={rootBorderClasses} type='single' collapsible>
      {children}
      {items
        .filter(item => item.links)
        .map((item, index) => (
          <AccordionItem
            value={`item-${item.label}`}
            key={`${item.label}-${index}`}
            className={itemBorderClasses}
          >
            <AccordionTrigger>
              <Text variant={triggerTextVariant}>
                {item.label}
                {item.new && <NewFooterTag />}
              </Text>
            </AccordionTrigger>
            <AccordionLinks
              className={linkBorderClasses}
              links={item.links}
              trackingEvent={trackingEvent}
              groupName={item.label}
            />
          </AccordionItem>
        ))}
    </Root>
  )
}
